import { useEffect, useState, useRef } from 'react';

const QuantumRevealGate = ({ children, onRevealComplete }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [progress, setProgress] = useState(0); // 0 to 100
  const [stage, setStage] = useState('INIT'); // INIT -> LOAD -> COMPLETE -> REVEAL

  const REVEAL_DURATION = 3500; // Total time in milliseconds
  const FRAMES_PER_SECOND = 60;
  const INTERVAL_MS = 1000 / FRAMES_PER_SECOND;

  // --- Mathematical Progress Implementation ---
  useEffect(() => {
    let progressInterval;
    let startTime = Date.now();
    setStage('LOAD');

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(1, elapsed / REVEAL_DURATION); // Time ratio (0 to 1)
      
      // Apply the Ease-Out Cubic function to the time ratio
      const newProgressValue = Math.min(100, easeOutCubic(t) * 100);
      
      setProgress(newProgressValue);

      if (newProgressValue >= 100) {
        clearInterval(progressInterval);
        setStage('COMPLETE');

        // Start the decomposition/glitch effect before full reveal
        setTimeout(() => {
            setIsRevealed(true);
            onRevealComplete?.();
        }, 500); // 500ms for glitch effect
      }
    };

    // Start the progress interval
    const initTimer = setTimeout(() => {
        progressInterval = setInterval(updateProgress, INTERVAL_MS);
    }, 100); // Small initial delay

    return () => {
      clearTimeout(initTimer);
      clearInterval(progressInterval);
    };
  }, [onRevealComplete]);

  // --- Render Logic ---

  if (isRevealed) {
    // Stage 4: Content Fade-In
    return (
      <div style={{
        opacity: 0,
        animation: 'contentFadeIn 1s ease-out 0.3s forwards'
      }}>
        {children}
      </div>
    );
  }

  // Stage 1-3: Loading Screen
  return (
    <div className={`fixed inset-0 z-[100] bg-slate-950 flex items-center justify-center overflow-hidden font-mono transition-opacity duration-300 ${stage === 'COMPLETE' ? 'opacity-0' : 'opacity-100'}`}>
        
        {/* 1. Background Grid & Data Flow */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(0, 255, 255, 0.05) 0%, transparent 70%)',
            backgroundSize: '400% 400%',
            animation: 'dataFlow 10s linear infinite'
        }}>
            {/* Animated Grid Lines - Simulating data flow */}
            <div className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(0,180,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,180,255,0.1)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20"></div>
        </div>

        {/* 2. Central Orb and Progress Lobe */}
        <div className="text-center relative z-10 p-12">
            
            {/* The Orb/Lobe - Scale and Glow tied to progress */}
            <div 
                className={`relative mx-auto rounded-full transition-all duration-300 ${stage === 'COMPLETE' ? 'animate-glitch-dissolve' : ''}`}
                style={{
                    width: `${60 + progress * 0.5}px`, // Slight expansion
                    height: `${60 + progress * 0.5}px`,
                    boxShadow: `0 0 ${progress * 0.25}px rgba(0, 255, 255, ${progress / 100})`, // Dynamic glow
                    transform: `rotateY(${progress * 7.2}deg)`, // Artistic rotation
                    border: `2px solid rgba(0, 255, 255, ${progress / 100})`
                }}
            >
                <div className="absolute inset-0 bg-cyan-500/10 rounded-full animate-pulse-slow"></div>
                {/* Center Text */}
                <div className="absolute inset-0 flex items-center justify-center text-xs text-white/80">
                    <span className="tabular-nums">
                        {Math.round(progress)}%
                    </span>
                </div>
            </div>

            {/* Title Display */}
            <div 
                className="text-2xl md:text-4xl font-bold text-white mt-10 tracking-widest"
                style={{
                    clipPath: `inset(0 ${100 - progress}% 0 0)`, // Text clip reveals with progress
                    transition: 'clip-path 0.1s linear' 
                }}
            >
                NIXQUE LAB
            </div>
            
            <div 
                className="text-sm text-cyan-400 tracking-wider mt-4 opacity-70"
                style={{ opacity: progress / 100 }}
            >
                [ {stage} ] COMPUTATIONAL MATRIX SYNCHRONIZING...
            </div>
        </div>

        {/* 3. System Messages (Fixed Terminal Style) */}
        <div className="absolute bottom-4 left-4 right-4 text-xs p-4 bg-slate-900/50 border border-slate-700/50 rounded backdrop-blur-sm shadow-xl max-w-lg mx-auto">
            <p><span className="text-yellow-400">[CORE]</span> Initializing **Fermat's Algorithm** for data integrity check...</p>
            <p className="mt-1"><span className="text-green-400">[INFO]</span> Quantum-State vector computation at <span className="text-white/80">{Math.round(progress / 10)}</span> TFLOPS.</p>
            <p className="mt-1"><span className={`transition-colors duration-300 ${progress < 90 ? 'text-cyan-400' : 'text-green-400'}`}>[STATUS]</span> System stability: {progress < 50 ? 'Low' : (progress < 95 ? 'Nominal' : 'Optimal')}</p>
        </div>

        {/* 4. CSS Animations */}
        <style jsx global>{`
            /* Content Fade In */
            @keyframes contentFadeIn {
                to { opacity: 1; }
            }
            
            /* Background Data Flow (Subtle movement) */
            @keyframes dataFlow {
                0% { background-position: 0% 0%; }
                100% { background-position: 400% 400%; }
            }

            /* Central Orb Pulse (Slow & Artistic) */
            @keyframes pulse-slow {
                0%, 100% { opacity: 0.1; }
                50% { opacity: 0.25; }
            }
            .animate-pulse-slow {
                animation: pulse-slow 8s ease-in-out infinite;
            }

            /* Decomposition/Glitch Effect (The high-concept finale) */
            @keyframes glitch-dissolve {
                0% { 
                    transform: scale(1) rotate(0deg); 
                    opacity: 1; 
                }
                25% { 
                    transform: scale(1.05) rotate(1deg); 
                    opacity: 0.8;
                    filter: hue-rotate(90deg);
                }
                50% { 
                    transform: translate(2px, 3px) skewX(2deg); 
                    opacity: 0.5;
                    clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%);
                }
                75% { 
                    transform: translate(-5px, -1px) skewX(-3deg) scale(0.98); 
                    opacity: 0.2;
                    clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0 100%);
                }
                100% { 
                    transform: scale(0.5); 
                    opacity: 0; 
                    filter: blur(50px);
                }
            }
            .animate-glitch-dissolve {
                animation: glitch-dissolve 0.5s ease-in-out forwards;
            }
        `}</style>
    </div>
  );
};

export default QuantumRevealGate;